/**
 * Textes de l'espace de saisie (ARTO-58) : `/finances`, `/jalons`, `/associes`
 * et l'élévation du second facteur. Fichier à part pour ne pas alourdir
 * `i18n.ts` ; ses blocs sont fusionnés dans le namespace `console` (D15).
 * Voix 2.2 : phrases courtes, un montant vide est « à saisir », jamais zéro.
 */

export const frSaisies = {
  finances: {
    titre: 'Finances',
    texte:
      'Tout ce qui se saisit à la main : abonnements, grille tarifaire, revenus ponctuels, sommes dues et coûts. Un montant laissé vide reste « à saisir » et n’entre dans aucun total.',
    onglets: {
      aria: 'Saisies financières',
      abonnements: 'Abonnements',
      tarifs: 'Grille tarifaire',
      revenus: 'Revenus ponctuels',
      creances: 'Sommes dues',
      couts: 'Coûts',
    },
    abonnements: {
      texte:
        'Un abonnement par galerie, ou un forfait pour un exploitant. Un abonnement ne se supprime pas : passez-le à « Résilié ».',
      ajouter: 'Ajouter un abonnement',
      vide: 'Aucun abonnement saisi pour l’instant.',
      colonnes: {
        galerie: 'Galerie',
        palier: 'Palier',
        montant: 'Montant mensuel',
        remise: 'Remise',
        tva: 'TVA',
        engagement: 'Engagement',
        debut: 'Début',
        etat: 'État',
      },
      champs: {
        nature: 'Type d’abonnement',
        groupe: 'Exploitant',
        galeriesForfait: 'Galeries du forfait',
        galerie: 'Galerie',
        palier: 'Palier',
        montant: 'Montant mensuel (€)',
        remise: 'Remise (%)',
        tva: 'Taux de TVA (%)',
        engagement: 'Engagement (mois)',
        debut: 'Début',
        fin: 'Fin',
        etat: 'État',
      },
      tvaAide:
        'Le montant mensuel est hors taxes. 20 % par défaut, 0 % si la TVA ne s’applique pas.',
      palierAide: 'Le tarif actif de ce palier, s’il est saisi, est proposé dans le montant.',
      etat: { essai: 'Essai', actif: 'Actif', suspendu: 'Suspendu', resilie: 'Résilié' },
      nature: { galerie: 'Galerie', forfait_exploitant: 'Forfait exploitant' },
      forfaitSansGalerie:
        'Cet exploitant n’a aucune galerie active : un forfait en demande au moins une.',
      forfaitPropose:
        'Le tarif actif de la tranche qui contient ce nombre de galeries, s’il est saisi, est proposé dans le montant.',
      forfait: 'Forfait exploitant',
      forfaitGaleries_one: '{{count}} galerie',
      forfaitGaleries_other: '{{count}} galeries',
      galerieInconnue: 'Galerie introuvable',
      mois_one: '{{count}} mois',
      mois_other: '{{count}} mois',
    },
    tarifs: {
      texte:
        'La grille propose un montant à la création d’un abonnement et découpe les forfaits par tranche. Elle ne modifie jamais un abonnement existant.',
      ajouter: 'Ajouter un tarif',
      vide: 'La grille est vide : saisissez les tarifs, un montant à la fois.',
      colonnes: {
        type: 'Type',
        libelle: 'Libellé',
        cible: 'Palier ou tranche',
        montant: 'Montant mensuel',
        actif: 'État',
        ordre: 'Ordre',
      },
      champs: {
        type: 'Type',
        libelle: 'Libellé',
        palier: 'Palier',
        min: 'À partir de (galeries)',
        max: 'Jusqu’à (galeries)',
        montant: 'Montant mensuel (€)',
        ordre: 'Ordre',
        actif: 'Actif',
      },
      maxAide: 'Laissez vide pour une tranche sans plafond.',
      type: { palier: 'Palier', forfait_exploitant: 'Forfait exploitant' },
      tranche: '{{min}} à {{max}} galeries',
      trancheSansPlafond: '{{min}} galeries et plus',
      actif: 'Actif',
      inactif: 'Inactif',
    },
    revenus: {
      texte: 'Ce qui n’est pas récurrent : mise en service, formation, développement sur mesure…',
      ajouter: 'Ajouter un revenu ponctuel',
      vide: 'Aucun revenu ponctuel saisi pour l’instant.',
      colonnes: {
        mois: 'Mois',
        service: 'Service',
        categorie: 'Catégorie',
        libelle: 'Libellé',
        montant: 'Montant HT',
        tva: 'TVA',
      },
      champs: {
        mois: 'Mois',
        service: 'Service',
        categorie: 'Catégorie',
        libelle: 'Libellé',
        montant: 'Montant HT (€)',
        tva: 'Taux de TVA (%)',
        galerie: 'Galerie',
      },
      aClasser: 'À classer',
      serviceAide:
        'Le service décide de la répartition. Laissé vide, le revenu reste « à classer » : il n’est pas réparti et le mois ne se clôture pas.',
      tvaAide: '20 % par défaut ; 0 % pour une autoliquidation ou un export.',
      galerieAide:
        'Facultatif : la galerie que la recette concerne. Seule une recette rattachée suit les galeries exclues de la comptabilité.',
      sansGalerie: 'Aucune',
    },
    creances: {
      texte: 'Ce que chaque client doit encore. Une somme n’est « réglée » que soldée.',
      ajouter: 'Ajouter une somme due',
      vide: 'Aucune somme due saisie pour l’instant.',
      colonnes: {
        echeance: 'Échéance',
        debiteur: 'Débiteur',
        libelle: 'Libellé',
        montant: 'Montant',
        encaisse: 'Encaissé',
        etat: 'État',
      },
      champs: {
        debiteur: 'Débiteur',
        libelle: 'Libellé',
        echeance: 'Échéance',
        montant: 'Montant (€)',
        encaisse: 'Encaissé (€)',
        etat: 'État',
        reglee: 'Réglée le',
      },
      etat: { due: 'Due', partielle: 'Partielle', reglee: 'Réglée', annulee: 'Annulée' },
    },
    couts: {
      texte: 'Les coûts du mois, poste par poste : la marge de la vue d’aigle en est tirée.',
      ajouter: 'Ajouter un coût',
      vide: 'Aucun coût saisi pour l’instant.',
      colonnes: { mois: 'Mois', poste: 'Poste', montant: 'Montant' },
      champs: { mois: 'Mois', poste: 'Poste', montant: 'Montant (€)' },
    },
  },
  jalons: {
    titre: 'Jalons',
    texte: 'Les étapes du projet, telles que les associés les voient.',
    ajouter: 'Ajouter un jalon',
    vide: 'Aucun jalon saisi pour l’instant.',
    colonnes: { ordre: 'Ordre', libelle: 'Libellé', echeance: 'Échéance', etat: 'État' },
    champs: { libelle: 'Libellé', echeance: 'Échéance', etat: 'État', ordre: 'Ordre' },
    etat: { a_venir: 'À venir', en_cours: 'En cours', atteint: 'Atteint', abandonne: 'Abandonné' },
    sansEcheance: 'Sans date',
  },
  associes: {
    titre: 'Associés',
    texte:
      'Les comptes associés de Venio ouvrent la vue d’aigle, en lecture seule, et rien d’autre.',
    inviter: 'Inviter un associé',
    invitation: {
      titre: 'Inviter un associé',
      texte:
        'L’associé reçoit un lien pour choisir son mot de passe. Il n’aura que la vue d’aigle, en lecture seule.',
      email: 'Adresse e-mail',
      nom: 'Nom',
      langue: 'Langue de l’invitation',
      envoyer: 'Envoyer l’invitation',
      envoi: 'Envoi…',
      succes: 'Invitation envoyée à {{email}}.',
      sansCourriel:
        'Le compte de {{email}} est créé, mais le courriel n’est pas parti. Invitez-le de nouveau.',
    },
    comptes: {
      titre: 'Comptes associés',
      vide: 'Aucun associé pour l’instant.',
      colonnes: {
        nom: 'Nom',
        email: 'Adresse e-mail',
        deuxFacteurs: 'Double authentification',
        derniereConnexion: 'Dernière connexion',
        actions: 'Actions',
      },
      deuxFacteursActive: 'Activée',
      deuxFacteursInactive: 'Non activée',
      invitationEnAttente: 'Invitation en attente',
      desactive: 'Désactivé',
      jamais: 'Jamais connecté',
      attente: 'En attente d’acceptation',
      desactiver: 'Désactiver',
      desactiverLigne: 'Désactiver {{nom}}',
      reactiver: 'Réactiver',
      reactiverLigne: 'Réactiver {{nom}}',
      reinitialiser: 'Réinitialiser la 2FA',
      reinitialiserLigne: 'Réinitialiser la 2FA de {{nom}}',
      annulerInvitation: 'Annuler l’invitation',
      annulerInvitationLigne: 'Annuler l’invitation de {{nom}}',
    },
    desactivation: {
      titre: 'Désactiver cet associé ?',
      texte:
        '{{nom}} ne pourra plus se connecter à la console. Son compte et son historique sont conservés : vous pourrez le réactiver.',
      garder: 'Garder actif',
      confirmer: 'Désactiver l’associé',
      enCours: 'Désactivation…',
    },
    annulation: {
      titre: 'Annuler l’invitation ?',
      texte:
        'Le lien envoyé à {{email}} ne servira plus. Vous pourrez inviter de nouveau cette adresse.',
      garder: 'Garder l’invitation',
      confirmer: 'Annuler l’invitation',
      enCours: 'Annulation…',
    },
    reinitialisation: {
      titre: 'Réinitialiser la double authentification ?',
      texte:
        '{{nom}} devra enrôler un nouvel appareil à sa prochaine connexion. Ses sessions ouvertes sont coupées.',
      confirmer: 'Réinitialiser la 2FA',
      enCours: 'Réinitialisation…',
    },
    partage: {
      titre: 'Partage entre associés',
      texte:
        'Les parts sont saisies, jamais calculées. Leur somme ne dépasse pas 100 %. Une part vide reste « à saisir ».',
      ajouter: 'Ajouter un bénéficiaire',
      vide: 'Aucun bénéficiaire pour l’instant.',
      colonnes: { beneficiaire: 'Bénéficiaire', compte: 'Compte associé', part: 'Part' },
      champs: { libelle: 'Bénéficiaire', compte: 'Compte associé', part: 'Part (%)' },
      aucunCompte: 'Aucun compte',
      total: 'Total des parts saisies : {{total}} % sur 100 %',
      partsASaisir_one: '{{count}} part à saisir',
      partsASaisir_other: '{{count}} parts à saisir',
    },
    versements: {
      titre: 'Versements du mois',
      texte:
        'Le dû et le versé sont saisis à la main. L’indicatif, part × marge, ne s’écrit jamais à votre place.',
      moisCloture:
        'Mois clôturé : le dû est celui de la clôture. Pour le corriger, rouvrez le mois dans la comptabilité ; le versé se saisit toujours.',
      mois: 'Mois',
      colonnes: {
        beneficiaire: 'Bénéficiaire',
        du: 'Dû',
        verse: 'Versé',
        verseLe: 'Versé le',
        indicatif: 'Indicatif',
      },
      champs: { du: 'Dû (€)', verse: 'Versé (€)', verseLe: 'Versé le' },
      saisir: 'Saisir',
      indicatif: 'indicatif : {{montant}}',
      indicatifAbsent: 'indicatif : marge du mois à saisir',
      vide: 'Ajoutez d’abord un bénéficiaire.',
    },
  },
  elevation: {
    titre: 'Vérification du second facteur',
    description:
      'Cette action exige une vérification récente du second facteur. Entrez le code de votre application d’authentification.',
    champCode: 'Code à six chiffres',
    champSecours: 'Code de secours',
    utiliserSecours: 'Utiliser un code de secours',
    utiliserCode: 'Utiliser le code de l’application',
    verifier: 'Vérifier',
    annuler: 'Annuler',
    fermer: 'Fermer',
    echec: 'Le code n’a pas pu être vérifié. Réessayez.',
  },
  saisies: {
    lectureSeule: 'Lecture seule',
    lectureSeuleAide: 'Votre rôle consulte cet onglet sans le modifier.',
    aSaisir: 'à saisir',
    absent: '—',
    chargement: 'Chargement…',
    erreurChargement: 'Impossible de charger ces saisies. Réessayez.',
    nouvelleLigne: 'Nouvelle ligne',
    modifierLigne: 'Modifier {{ligne}}',
    supprimerLigne: 'Supprimer {{ligne}}',
    enregistrer: 'Enregistrer',
    enregistrement: 'Enregistrement…',
    annuler: 'Annuler',
    actions: 'Actions',
    aucun: 'Aucun',
    choisir: 'Choisir…',
    modifier: 'Modifier',
    supprimer: 'Supprimer',
    visibleAssocies: 'Visible des associés : n’y écrivez pas de nom de client.',
    sansMontant_one: '{{count}} ligne sans montant n’est comptée dans aucun total.',
    sansMontant_other: '{{count}} lignes sans montant ne sont comptées dans aucun total.',
    suppression: {
      titre: 'Supprimer cette ligne ?',
      texte: '« {{ligne}} » sera supprimée définitivement.',
      confirmer: 'Supprimer',
      enCours: 'Suppression…',
      fermer: 'Fermer',
    },
    erreursChamp: {
      requis: 'À renseigner.',
      montant: 'Montant attendu, par exemple 1 234,50.',
      entier: 'Nombre entier attendu, sans décimale.',
      pourcentage: 'Pourcentage attendu, de 0 à 100, par exemple 12,5.',
      date: 'Date attendue.',
      mois: 'Mois attendu : AAAA-MM.',
      longueur: '{{max}} caractères au plus.',
    },
    erreurs: {
      doublon: {
        defaut: 'Cette ligne existe déjà : modifiez la ligne existante plutôt que d’en créer une.',
        couts: 'Ce poste a déjà un coût pour ce mois : modifiez la ligne existante.',
      },
      creanceNonSoldee:
        'Une somme due ne passe à « réglée » que soldée : saisissez le montant et l’encaissement.',
      partsDepassees: 'La somme des parts dépasserait 100 % : baissez d’abord une autre part.',
      versementsExistants: 'Ce bénéficiaire a des versements : il ne peut pas être supprimé.',
      versementExistant: 'Un versement existe déjà pour ce bénéficiaire et ce mois : modifiez-le.',
      roleExistant:
        'Cette adresse appartient déjà à un compte de la console : elle ne peut pas être invitée comme associé.',
      compteEnSuppression: 'Ce compte est en cours de suppression : il ne peut pas être invité.',
      invitationConcurrente: 'Cette adresse vient d’être invitée : rechargez la liste.',
      elevationRefusee: 'La vérification n’a pas été acceptée, réessayez.',
      moisCloture_one:
        'Le mois {{mois}} est clôturé : rouvrez-le avant d’y toucher (page Comptabilité).',
      moisCloture_other:
        'Les mois {{mois}} sont clôturés : rouvrez-les avant d’y toucher (page Comptabilité).',
      moisCloture_abonnements_one:
        'Cet abonnement compte dans le mois clôturé {{mois}} : il n’y change plus. Pour un nouveau tarif, résiliez-le à une date d’un mois ouvert et créez-en un autre ; pour corriger le passé, rouvrez le mois (page Comptabilité).',
      moisCloture_abonnements_other:
        'Cet abonnement compte dans les mois clôturés {{mois}} : il n’y change plus. Pour un nouveau tarif, résiliez-le à une date d’un mois ouvert et créez-en un autre ; pour corriger le passé, rouvrez ces mois (page Comptabilité).',
      moisCloture_versements_one:
        'Le mois {{mois}} est clôturé : son dû est celui de la clôture. Rouvrez-le pour le corriger (page Comptabilité) ; le versé se saisit toujours.',
      moisCloture_versements_other:
        'Les mois {{mois}} sont clôturés : leur dû est celui de la clôture. Rouvrez-les pour le corriger (page Comptabilité) ; le versé se saisit toujours.',
      versionExistante:
        'Une version de ce service commence déjà ce mois-là : elle est corrigée, pas doublée. Rechargez la page.',
      clotureImpossible:
        'Le mois n’est pas complet : saisissez les montants, classez les recettes et donnez une grille à chaque service qui a des recettes.',
      moisNonTermine:
        'Ce mois n’est pas terminé : il se clôture à partir du premier jour du mois suivant.',
      moisDejaCloture: 'Ce mois est déjà clôturé.',
      repartitionSomme: 'Les parts font {{somme}} % : elles doivent faire exactement 100 %.',
      repartitionDouble: 'Un bénéficiaire figure deux fois dans cette version.',
      repartitionInconnu: 'Ce bénéficiaire n’existe plus dans le partage : rechargez la page.',
      resteDejaDesigne: 'Un autre bénéficiaire reçoit déjà le reste : rechargez la page.',
      resteAvecExclusions:
        'Ce bénéficiaire exclut des galeries : retirez ses exclusions avant de lui donner le reste.',
      exclusionDuReste: 'Le bénéficiaire du reste ne peut pas exclure de galerie.',
      exclusionGalerieTest: 'Un espace de test n’entre dans aucun chiffre : rien à exclure.',
      exclusionExistante:
        'Ce bénéficiaire exclut déjà cette galerie : retirez l’exclusion pour en changer le mois.',
      compteNonAssocie: 'Ce compte n’est pas un compte associé.',
      invalide: 'Certains champs sont invalides : {{champs}}.',
      invalideSansChamp: 'Les données envoyées sont invalides.',
      droits: 'Votre rôle ne permet pas cette saisie.',
      introuvable: 'Cette ligne n’existe plus : rechargez la page.',
      reseau: 'Impossible de joindre le serveur. Réessayez.',
      inconnue: 'La saisie n’a pas abouti. Réessayez.',
    },
  },
};

export const enSaisies = {
  finances: {
    titre: 'Finances',
    texte:
      'Everything that is entered by hand: subscriptions, tariff grid, one-off revenue, amounts due and costs. An amount left empty stays “to be entered” and counts in no total.',
    onglets: {
      aria: 'Financial entries',
      abonnements: 'Subscriptions',
      tarifs: 'Tariff grid',
      revenus: 'One-off revenue',
      creances: 'Amounts due',
      couts: 'Costs',
    },
    abonnements: {
      texte:
        'One subscription per gallery, or a flat fee for an operator. A subscription is never deleted: set it to “Terminated”.',
      ajouter: 'Add a subscription',
      vide: 'No subscription entered yet.',
      colonnes: {
        galerie: 'Gallery',
        palier: 'Tier',
        montant: 'Monthly amount',
        remise: 'Discount',
        tva: 'VAT',
        engagement: 'Commitment',
        debut: 'Start',
        etat: 'State',
      },
      champs: {
        nature: 'Subscription type',
        groupe: 'Operator',
        galeriesForfait: 'Galleries in the flat fee',
        galerie: 'Gallery',
        palier: 'Tier',
        montant: 'Monthly amount (€)',
        remise: 'Discount (%)',
        tva: 'VAT rate (%)',
        engagement: 'Commitment (months)',
        debut: 'Start',
        fin: 'End',
        etat: 'State',
      },
      tvaAide: 'The monthly amount excludes tax. 20% by default, 0% when VAT does not apply.',
      palierAide: 'The active tariff for this tier, if entered, is suggested as the amount.',
      etat: { essai: 'Trial', actif: 'Active', suspendu: 'Suspended', resilie: 'Terminated' },
      nature: { galerie: 'Gallery', forfait_exploitant: 'Operator flat fee' },
      forfaitSansGalerie: 'This operator has no active gallery: a flat fee needs at least one.',
      forfaitPropose:
        'The active tariff of the band that contains this number of galleries, if entered, is suggested as the amount.',
      forfait: 'Operator flat fee',
      forfaitGaleries_one: '{{count}} gallery',
      forfaitGaleries_other: '{{count}} galleries',
      galerieInconnue: 'Gallery not found',
      mois_one: '{{count}} month',
      mois_other: '{{count}} months',
    },
    tarifs: {
      texte:
        'The grid suggests an amount when a subscription is created and splits flat fees into bands. It never changes an existing subscription.',
      ajouter: 'Add a tariff',
      vide: 'The grid is empty: enter the tariffs, one amount at a time.',
      colonnes: {
        type: 'Type',
        libelle: 'Label',
        cible: 'Tier or band',
        montant: 'Monthly amount',
        actif: 'State',
        ordre: 'Order',
      },
      champs: {
        type: 'Type',
        libelle: 'Label',
        palier: 'Tier',
        min: 'From (galleries)',
        max: 'Up to (galleries)',
        montant: 'Monthly amount (€)',
        ordre: 'Order',
        actif: 'Active',
      },
      maxAide: 'Leave empty for a band with no upper limit.',
      type: { palier: 'Tier', forfait_exploitant: 'Operator flat fee' },
      tranche: '{{min}} to {{max}} galleries',
      trancheSansPlafond: '{{min}} galleries and more',
      actif: 'Active',
      inactif: 'Inactive',
    },
    revenus: {
      texte: 'What is not recurring: setup, training, custom development…',
      ajouter: 'Add one-off revenue',
      vide: 'No one-off revenue entered yet.',
      colonnes: {
        mois: 'Month',
        service: 'Service',
        categorie: 'Category',
        libelle: 'Label',
        montant: 'Amount excl. tax',
        tva: 'VAT',
      },
      champs: {
        mois: 'Month',
        service: 'Service',
        categorie: 'Category',
        libelle: 'Label',
        montant: 'Amount excl. tax (€)',
        tva: 'VAT rate (%)',
        galerie: 'Gallery',
      },
      aClasser: 'To classify',
      serviceAide:
        'The service decides the split. Left empty, the revenue stays “to classify”: it is not split and the month cannot be closed.',
      tvaAide: '20% by default; 0% for reverse charge or an export.',
      galerieAide:
        'Optional: the gallery the revenue concerns. Only linked revenue follows the excluded galleries of the accounts.',
      sansGalerie: 'None',
    },
    creances: {
      texte: 'What each client still owes. An amount is only “settled” once paid in full.',
      ajouter: 'Add an amount due',
      vide: 'No amount due entered yet.',
      colonnes: {
        echeance: 'Due date',
        debiteur: 'Debtor',
        libelle: 'Label',
        montant: 'Amount',
        encaisse: 'Received',
        etat: 'State',
      },
      champs: {
        debiteur: 'Debtor',
        libelle: 'Label',
        echeance: 'Due date',
        montant: 'Amount (€)',
        encaisse: 'Received (€)',
        etat: 'State',
        reglee: 'Settled on',
      },
      etat: { due: 'Due', partielle: 'Partial', reglee: 'Settled', annulee: 'Cancelled' },
    },
    couts: {
      texte: 'The month’s costs, line by line: the overview’s margin is drawn from them.',
      ajouter: 'Add a cost',
      vide: 'No cost entered yet.',
      colonnes: { mois: 'Month', poste: 'Item', montant: 'Amount' },
      champs: { mois: 'Month', poste: 'Item', montant: 'Amount (€)' },
    },
  },
  jalons: {
    titre: 'Milestones',
    texte: 'The project’s stages, as partners see them.',
    ajouter: 'Add a milestone',
    vide: 'No milestone entered yet.',
    colonnes: { ordre: 'Order', libelle: 'Label', echeance: 'Due date', etat: 'State' },
    champs: { libelle: 'Label', echeance: 'Due date', etat: 'State', ordre: 'Order' },
    etat: {
      a_venir: 'Upcoming',
      en_cours: 'In progress',
      atteint: 'Reached',
      abandonne: 'Dropped',
    },
    sansEcheance: 'No date',
  },
  associes: {
    titre: 'Partners',
    texte: 'Venio partner accounts open the overview, read-only, and nothing else.',
    inviter: 'Invite a partner',
    invitation: {
      titre: 'Invite a partner',
      texte:
        'The partner receives a link to choose a password. They will only have the overview, read-only.',
      email: 'Email address',
      nom: 'Name',
      langue: 'Invitation language',
      envoyer: 'Send the invitation',
      envoi: 'Sending…',
      succes: 'Invitation sent to {{email}}.',
      sansCourriel:
        'The account for {{email}} is created, but the email did not go out. Invite again.',
    },
    comptes: {
      titre: 'Partner accounts',
      vide: 'No partners yet.',
      colonnes: {
        nom: 'Name',
        email: 'Email address',
        deuxFacteurs: 'Two-factor authentication',
        derniereConnexion: 'Last sign-in',
        actions: 'Actions',
      },
      deuxFacteursActive: 'Enabled',
      deuxFacteursInactive: 'Not enabled',
      invitationEnAttente: 'Invitation pending',
      desactive: 'Deactivated',
      jamais: 'Never signed in',
      attente: 'Waiting for acceptance',
      desactiver: 'Deactivate',
      desactiverLigne: 'Deactivate {{nom}}',
      reactiver: 'Reactivate',
      reactiverLigne: 'Reactivate {{nom}}',
      reinitialiser: 'Reset 2FA',
      reinitialiserLigne: 'Reset the 2FA of {{nom}}',
      annulerInvitation: 'Cancel invitation',
      annulerInvitationLigne: 'Cancel the invitation of {{nom}}',
    },
    desactivation: {
      titre: 'Deactivate this partner?',
      texte:
        '{{nom}} will no longer be able to sign in to the console. Their account and history are kept: you can turn it back on.',
      garder: 'Keep active',
      confirmer: 'Deactivate partner',
      enCours: 'Deactivating…',
    },
    annulation: {
      titre: 'Cancel the invitation?',
      texte: 'The link sent to {{email}} will no longer work. You can invite this address again.',
      garder: 'Keep the invitation',
      confirmer: 'Cancel invitation',
      enCours: 'Cancelling…',
    },
    reinitialisation: {
      titre: 'Reset two-factor authentication?',
      texte: '{{nom}} will have to enrol a new device at the next sign-in. Open sessions are cut.',
      confirmer: 'Reset 2FA',
      enCours: 'Resetting…',
    },
    partage: {
      titre: 'Profit share',
      texte:
        'Shares are entered, never computed. Their sum never exceeds 100%. An empty share stays “to be entered”.',
      ajouter: 'Add a beneficiary',
      vide: 'No beneficiaries yet.',
      colonnes: { beneficiaire: 'Beneficiary', compte: 'Partner account', part: 'Share' },
      champs: { libelle: 'Beneficiary', compte: 'Partner account', part: 'Share (%)' },
      aucunCompte: 'No account',
      total: 'Total shares entered: {{total}}% of 100%',
      partsASaisir_one: '{{count}} share to enter',
      partsASaisir_other: '{{count}} shares to enter',
    },
    versements: {
      titre: 'Payouts for the month',
      texte:
        'Amounts due and paid are entered by hand. The indicative figure, share × margin, is never written for you.',
      moisCloture:
        'Closed month: the amount due is the one set at closing. To correct it, reopen the month in the accounts; amounts paid can still be entered.',
      mois: 'Month',
      colonnes: {
        beneficiaire: 'Beneficiary',
        du: 'Due',
        verse: 'Paid',
        verseLe: 'Paid on',
        indicatif: 'Indicative',
      },
      champs: { du: 'Due (€)', verse: 'Paid (€)', verseLe: 'Paid on' },
      saisir: 'Enter',
      indicatif: 'indicative: {{montant}}',
      indicatifAbsent: 'indicative: the month’s margin is yet to be entered',
      vide: 'Add a beneficiary first.',
    },
  },
  elevation: {
    titre: 'Second-factor check',
    description:
      'This action requires a recent second-factor check. Enter the code from your authenticator app.',
    champCode: 'Six-digit code',
    champSecours: 'Backup code',
    utiliserSecours: 'Use a backup code',
    utiliserCode: 'Use the app code',
    verifier: 'Verify',
    annuler: 'Cancel',
    fermer: 'Close',
    echec: 'The code could not be verified. Try again.',
  },
  saisies: {
    lectureSeule: 'Read-only',
    lectureSeuleAide: 'Your role can view this tab but not edit it.',
    aSaisir: 'to be entered',
    absent: '—',
    chargement: 'Loading…',
    erreurChargement: 'These entries could not be loaded. Try again.',
    nouvelleLigne: 'New line',
    modifierLigne: 'Edit {{ligne}}',
    supprimerLigne: 'Delete {{ligne}}',
    enregistrer: 'Save',
    enregistrement: 'Saving…',
    annuler: 'Cancel',
    actions: 'Actions',
    aucun: 'None',
    choisir: 'Choose…',
    modifier: 'Edit',
    supprimer: 'Delete',
    visibleAssocies: 'Visible to partners: do not enter client names.',
    sansMontant_one: '{{count}} line without an amount is counted in no total.',
    sansMontant_other: '{{count}} lines without an amount are counted in no total.',
    suppression: {
      titre: 'Delete this line?',
      texte: '“{{ligne}}” will be deleted for good.',
      confirmer: 'Delete',
      enCours: 'Deleting…',
      fermer: 'Close',
    },
    erreursChamp: {
      requis: 'Required.',
      montant: 'Amount expected, for example 1,234.50.',
      entier: 'Whole number expected, no decimals.',
      pourcentage: 'Percentage expected, from 0 to 100, for example 12.5.',
      date: 'Date expected.',
      mois: 'Month expected: YYYY-MM.',
      longueur: '{{max}} characters at most.',
    },
    erreurs: {
      doublon: {
        defaut: 'This line already exists: edit the existing line instead of creating another.',
        couts: 'This item already has a cost for that month: edit the existing line.',
      },
      creanceNonSoldee:
        'An amount due is only “settled” once paid in full: enter the amount and the amount received.',
      partsDepassees: 'The shares would add up to more than 100%: lower another share first.',
      versementsExistants: 'This beneficiary has payouts: they cannot be deleted.',
      versementExistant: 'A payout already exists for this beneficiary and month: edit it.',
      roleExistant:
        'This address already belongs to a console account: it cannot be invited as a partner.',
      compteEnSuppression: 'This account is being deleted: it cannot be invited.',
      invitationConcurrente: 'This address was just invited: reload the list.',
      elevationRefusee: 'The check was not accepted, try again.',
      moisCloture_one:
        'The month {{mois}} is closed: reopen it before changing it (Accounting page).',
      moisCloture_other:
        'The months {{mois}} are closed: reopen them before changing them (Accounting page).',
      moisCloture_abonnements_one:
        'This subscription counts in the closed month {{mois}}: it no longer changes there. For a new price, end it on a date in an open month and create another; to correct the past, reopen the month (Accounting page).',
      moisCloture_abonnements_other:
        'This subscription counts in the closed months {{mois}}: it no longer changes there. For a new price, end it on a date in an open month and create another; to correct the past, reopen those months (Accounting page).',
      moisCloture_versements_one:
        'The month {{mois}} is closed: its amount due is the one set at closing. Reopen it to correct it (Accounting page); amounts paid can still be entered.',
      moisCloture_versements_other:
        'The months {{mois}} are closed: their amounts due are the ones set at closing. Reopen them to correct them (Accounting page); amounts paid can still be entered.',
      versionExistante:
        'A version of this service already starts that month: it is corrected, not duplicated. Reload the page.',
      clotureImpossible:
        'The month is not complete: enter the amounts, classify the revenue and give every service that has revenue a split.',
      moisNonTermine:
        'This month is not over: it can be closed from the first day of the next month.',
      moisDejaCloture: 'This month is already closed.',
      repartitionSomme: 'The shares add up to {{somme}}%: they must add up to exactly 100%.',
      repartitionDouble: 'A beneficiary appears twice in this version.',
      repartitionInconnu: 'This beneficiary no longer exists in the split: reload the page.',
      resteDejaDesigne: 'Another beneficiary already receives the rest: reload the page.',
      resteAvecExclusions:
        'This beneficiary excludes galleries: remove the exclusions before giving them the rest.',
      exclusionDuReste: 'The beneficiary who receives the rest cannot exclude a gallery.',
      exclusionGalerieTest: 'A test space is in no figure: nothing to exclude.',
      exclusionExistante:
        'This beneficiary already excludes this gallery: remove the exclusion to change its month.',
      compteNonAssocie: 'This account is not a partner account.',
      invalide: 'Some fields are invalid: {{champs}}.',
      invalideSansChamp: 'The data sent is invalid.',
      droits: 'Your role does not allow this entry.',
      introuvable: 'This line no longer exists: reload the page.',
      reseau: 'Could not reach the server. Try again.',
      inconnue: 'The entry did not go through. Try again.',
    },
  },
};
